import _classCallCheck from "/mnt/ramdisk/app/solvuu-ui/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _possibleConstructorReturn from "/mnt/ramdisk/app/solvuu-ui/node_modules/@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "/mnt/ramdisk/app/solvuu-ui/node_modules/@babel/runtime/helpers/esm/getPrototypeOf";
import _assertThisInitialized from "/mnt/ramdisk/app/solvuu-ui/node_modules/@babel/runtime/helpers/esm/assertThisInitialized";
import _inherits from "/mnt/ramdisk/app/solvuu-ui/node_modules/@babel/runtime/helpers/esm/inherits";
import _toConsumableArray from "/mnt/ramdisk/app/solvuu-ui/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "/mnt/ramdisk/app/solvuu-ui/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import jStat from "jStat";
import { stats, lin } from "science";
import { min, max } from "d3-array";
import { scaleLinear } from "d3-scale";
import { countBy, toPairs, range, sum, times } from "ramda";
import cluster from "hierarchical-clustering";
import kmeans from "ml-kmeans";
import Matrix, { SingularValueDecomposition } from "ml-matrix";
import { kendall_tau as kendallTau } from "./science0.bs.js";
export function categoricalSeriesToHistogram(series) {
  var grouped = countBy(function (s) {
    return s;
  }, series);
  return toPairs(grouped).map(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        name = _ref2[0],
        count = _ref2[1];

    return {
      name: name,
      count: count
    };
  });
}
export function numericSeriesToKernelDensity(series) {
  var bandwidthFn = stats.bandwidth.nrd;
  var bandwidthEstimated = bandwidthFn(series);
  var bandwidth = bandwidthEstimated === 0 ? 1 : bandwidthEstimated;
  var domain = [min(series) - 3 * bandwidth, max(series) + 3 * bandwidth];
  var points = scaleLinear().domain(domain).ticks(1000);
  var kde = stats.kde().sample(series).bandwidth(bandwidth)(points);
  return kde.map(function (_ref3) {
    var _ref4 = _slicedToArray(_ref3, 2),
        x = _ref4[0],
        y = _ref4[1];

    return {
      x: x,
      y: y
    };
  });
}
export function normalizeMatrix(matrix) {
  // Z-Score normalization by column
  // Mean = 0, Variance = 1
  return matrix.map(normalizeZScore);
}

function normalizeZScore(vector) {
  var mean = stats.mean(vector);
  var stddev = Math.sqrt(stats.variance(vector));
  return vector.map(function (value) {
    return (value - mean) / (stddev || 1);
  });
}

export function transposeMatrix(matrix) {
  return lin.transpose(matrix);
}
export function hierarchicalClustering(normalizedMatrix, linkageFn, distanceFn) {
  var alongXAxis = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  var input = alongXAxis ? lin.transpose(normalizedMatrix) : normalizedMatrix;
  var levels = cluster({
    input: input,
    distance: distanceFn,
    linkage: linkageFn
  });
  return levels;
}
export function kMeansClustering(normalizedMatrix, distanceFn, numClusters) {
  var alongXAxis = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  var input = alongXAxis ? lin.transpose(normalizedMatrix) : normalizedMatrix;
  var output = kmeans(input, numClusters, {
    distanceFunction: distanceFn,
    seed: 0
  });
  var assignments = output.clusters;
  var clusters = times(function () {
    return [];
  }, numClusters);
  assignments.forEach(function (clusterIndex, inputIndex) {
    clusters[clusterIndex].push(inputIndex);
  });
  return [{
    clusters: clusters
  }];
}

function centroid(listOfVectors) {
  return lin.transpose(listOfVectors).map(function (v) {
    return stats.mean(v);
  });
}
/**
 * Linkage criteria
 * https://uc-r.github.io/hc_clustering
 */


export var meanLinkage = cluster.linkages.average;
export var completeLinkage = cluster.linkages.complete;
export var singleLinkage = cluster.linkages.single;
export function centroidLinkage(pairwiseDistances, distanceMetric, clusterA, clusterB) {
  var centroidA = centroid(clusterA);
  var centroidB = centroid(clusterB);
  return distanceMetric(centroidA, centroidB);
} // https://en.wikipedia.org/wiki/Ward%27s_method
// https://www.stat.cmu.edu/~cshalizi/350/lectures/08/lecture-08.pdf

export function wardLinkage(_pairwiseDistances, _distanceMetric, clusterA, clusterB) {
  var clusterJoined = [].concat(_toConsumableArray(clusterA), _toConsumableArray(clusterB));
  return clusterSumOfSquares(clusterJoined) - clusterSumOfSquares(clusterA) - clusterSumOfSquares(clusterB);
}

function clusterSumOfSquares(cluster) {
  var mean = centroid(cluster);
  return sum(cluster.map(function (vector) {
    return sum(vector.map(function (el, i) {
      return Math.pow(el - mean[i], 2);
    }));
  }));
}
/**
 * Distance metrics
 * https://uc-r.github.io/kmeans_clustering#distance
 */


export var euclideanDistance = stats.distance.euclidean;
export var manhattanDistance = stats.distance.manhattan;
export var maximumDistance = stats.distance.chebyshev;
export function pearsonCorrelationDistance(a, b) {
  // Correlation coefficient may be NaN when any of the inputs is constant (stddev = 0).
  // We assume no correlation (0) in such case.
  return 1 - (jStat.corrcoeff(a, b) || 0);
}
export function spearmanCorrelationDistance(a, b) {
  return 1 - (jStat.spearmancoeff(a, b) || 0);
}
export function kendallCorrelationDistance(a, b) {
  return 1 - kendallTau(a, b);
} // Override the internal linkageOf function from the hierarchical-clustering package
// so that the linkage function gets passed the distance function, and both clusters
// in addition to pairwise distances.

cluster.prototype.linkageOf = function (clusterA, clusterB) {
  var _this = this;

  var hash = clusterA.length > clusterB.length ? "" + clusterA + "-" + clusterB : "" + clusterB + "-" + clusterA;
  var links = this.links;
  if (hash in links) return links[hash]; // grab all the distances

  var distances = [];

  for (var k = 0; k < clusterA.length; k++) {
    for (var h = 0; h < clusterB.length; h++) {
      distances.push(this.distanceOf(clusterA[k], clusterB[h]));
    }
  } // cache and return the linkage


  return links[hash] = this.linkage(distances, this.distance, clusterA.map(function (i) {
    return _this.input[i];
  }), clusterB.map(function (i) {
    return _this.input[i];
  }));
};

export function pearsonCorrelationCoefficient(x, y) {
  var coefficient = jStat.corrcoeff(x, y);
  return !isNaN(coefficient) ? coefficient : undefined;
} // HACK: Assign the matrix prototype directly to Matrix objects from ml-matrix
// Without this, ml-matrix does not work correctly (undefined method transpose).
// I think it's because we transpile the code with babel, and ml-matrix extends
// native Array, which babel can't handle.
// https://github.com/mljs/matrix/issues/20

var matrixPrototype = Matrix.prototype;

var NewMatrix =
/*#__PURE__*/
function (_Matrix) {
  _inherits(NewMatrix, _Matrix);

  function NewMatrix(nRows, nColumns) {
    var _this2;

    _classCallCheck(this, NewMatrix);

    _this2 = _possibleConstructorReturn(this, _getPrototypeOf(NewMatrix).call(this, nRows, nColumns));
    Object.setPrototypeOf(_assertThisInitialized(_this2), matrixPrototype);
    return _this2;
  }

  return NewMatrix;
}(Matrix);

Matrix.prototype.constructor = NewMatrix; // https://en.wikipedia.org/wiki/Multidimensional_scaling#Classical_multidimensional_scaling
// https://github.com/benfred/mds.js/blob/master/mds.js

export function calculatePCoA(input, distanceFn) {
  var M = NewMatrix;
  var n = input.length;
  var axis = range(0, n); // Construct the distance (dissimilarity) matrix

  var D = axis.map(function (y) {
    return axis.map(function (x) {
      return distanceFn(input[x], input[y]);
    });
  });
  var Dsquared = M.pow(D, 2); // Apply double centering

  var I = M.eye(n);
  var J = M.sub(I, M.mul(M.ones(n, n), 1 / n));
  var B = M.mul(J, -0.5).mmul(Dsquared).mmul(J); // Matrix decomposition

  var svd = new SingularValueDecomposition(B);
  var E = new M(svd.leftSingularVectors);
  var A = M.diag(svd.diagonal); // Final coordinates matrix

  var X = E.mmul(M.pow(A, 0.5));
  var explainedVariance = A.diag().map(function (e) {
    return e / A.sum();
  });
  var predictions = X.to2DArray();
  return {
    explainedVariance: explainedVariance,
    predictions: predictions
  };
}
export function calculatePercentiles(input) {
  var quantiles = range(1, 100).map(function (p) {
    return p / 100;
  }); // We're using 1th through 99th percentiles and skipping 0th and 100th.
  // The 0th and 100th percentiles may be infinite for certain distributions (e.g. normal).

  console.assert(quantiles.length === 99);
  return stats.quantiles(input, quantiles);
} // Generated with R:
// qnorm(seq(0.01, 0.99, by=0.01)

export var normalDistributionPercentiles = [-2.32634787404084, -2.05374891063182, -1.88079360815125, -1.75068607125217, -1.64485362695147, -1.55477359459685, -1.47579102817917, -1.40507156030963, -1.34075503369022, -1.2815515655446, -1.22652812003661, -1.17498679206609, -1.1263911290388, -1.08031934081496, -1.03643338949379, -0.994457883209753, -0.954165253146194, -0.915365087842814, -0.877896295051228, -0.841621233572914, -0.80642124701824, -0.772193214188685, -0.738846849185214, -0.706302562840088, -0.674489750196082, -0.643345405392917, -0.612812991016627, -0.582841507271216, -0.553384719555673, -0.524400512708041, -0.495850347347453, -0.467698799114508, -0.439913165673234, -0.412463129441405, -0.385320466407568, -0.358458793251194, -0.331853346436817, -0.305480788099397, -0.279319034447454, -0.2533471031358, -0.227544976641149, -0.201893479141851, -0.176374164780861, -0.150969215496777, -0.125661346855074, -0.10043372051147, -0.0752698620998298, -0.0501535834647335, -0.0250689082587111, 0, 0.0250689082587111, 0.0501535834647337, 0.0752698620998299, 0.10043372051147, 0.125661346855074, 0.150969215496777, 0.176374164780861, 0.201893479141851, 0.227544976641149, 0.2533471031358, 0.279319034447454, 0.305480788099397, 0.331853346436817, 0.358458793251194, 0.385320466407568, 0.412463129441405, 0.439913165673234, 0.467698799114508, 0.495850347347453, 0.524400512708041, 0.553384719555673, 0.582841507271216, 0.612812991016627, 0.643345405392917, 0.674489750196082, 0.706302562840088, 0.738846849185214, 0.772193214188685, 0.80642124701824, 0.841621233572914, 0.877896295051229, 0.915365087842814, 0.954165253146195, 0.994457883209753, 1.03643338949379, 1.08031934081496, 1.1263911290388, 1.17498679206609, 1.22652812003661, 1.2815515655446, 1.34075503369022, 1.40507156030963, 1.47579102817917, 1.55477359459685, 1.64485362695147, 1.75068607125217, 1.88079360815125, 2.05374891063182, 2.32634787404084];
console.assert(normalDistributionPercentiles.length === 99);
console.assert(normalDistributionPercentiles[49] === 0); // Generated with R
// write.csv(-log10(qunif(seq(0.99, 0.01, by=-0.01))), row.names=FALSE, eol=",")

export var minusLog10UniformDistributionPercentiles = [0.00436480540245009, 0.00877392430750515, 0.0132282657337552, 0.0177287669604316, 0.0222763947111523, 0.0268721464003014, 0.0315170514460649, 0.0362121726544448, 0.0409586076789064, 0.0457574905606751, 0.0506099933550872, 0.0555173278498314, 0.0604807473813815, 0.0655015487564323, 0.0705810742857073, 0.0757207139381184, 0.0809219076239261, 0.0861861476162833, 0.0915149811213502, 0.0969100130080564, 0.102372908709559, 0.10790539730952, 0.113509274827518, 0.119186407719209, 0.1249387366083, 0.130768280269024, 0.136677139879544, 0.142667503568732, 0.148741651280925, 0.154901959985743, 0.161150909262745, 0.167491087293764, 0.173925197299174, 0.180456064458131, 0.187086643357144, 0.193820026016113, 0.200659450546418, 0.207608310501746, 0.214670164989233, 0.221848749616356, 0.229147988357856, 0.236572006437063, 0.244125144327509, 0.2518119729938, 0.259637310505756, 0.267606240177031, 0.275724130399211, 0.283996656365201, 0.292429823902064, 0.301029995663981, 0.309803919971486, 0.318758762624413, 0.327902142064283, 0.337242168318426, 0.346787486224656, 0.356547323513813, 0.366531544420414, 0.3767507096021, 0.387216143280264, 0.397940008672038, 0.408935392973501, 0.42021640338319, 0.431798275933005, 0.443697499232713, 0.455931955649724, 0.468521082957745, 0.481486060122113, 0.494850021680094, 0.508638306165727, 0.522878745280338, 0.537602002101044, 0.552841968657781, 0.568636235841013, 0.585026652029182, 0.602059991327962, 0.619788758288394, 0.638272163982407, 0.657577319177794, 0.677780705266081, 0.698970004336019, 0.721246399047171, 0.744727494896694, 0.769551078621726, 0.795880017344075, 0.823908740944319, 0.853871964321762, 0.886056647693163, 0.920818753952375, 0.958607314841775, 1, 1.04575749056068, 1.09691001300806, 1.15490195998574, 1.22184874961636, 1.30102999566398, 1.39794000867204, 1.52287874528034, 1.69897000433602, 2];
console.assert(minusLog10UniformDistributionPercentiles.length === 99); // Generated with R
// write.csv(log2(qunif(seq(0.01, 0.99, by=0.01))), row.names=FALSE, eol=",")

export var log2UniformDistributionPercentiles = [-6.64385618977472, -5.64385618977472, -5.05889368905357, -4.64385618977472, -4.32192809488736, -4.05889368905357, -3.83650126771712, -3.64385618977472, -3.47393118833241, -3.32192809488736, -3.18442457113743, -3.05889368905357, -2.94341647163363, -2.83650126771712, -2.73696559416621, -2.64385618977472, -2.55639334852439, -2.47393118833241, -2.39592867633114, -2.32192809488736, -2.25153876699596, -2.18442457113743, -2.12029423371771, -2.05889368905357, -2, -1.94341647163363, -1.88896868761126, -1.83650126771712, -1.78587519464715, -1.73696559416621, -1.68965987938785, -1.64385618977472, -1.59946207041627, -1.55639334852439, -1.51457317282976, -1.47393118833241, -1.43440282414577, -1.39592867633114, -1.35845397091248, -1.32192809488736, -1.28630418515664, -1.25153876699596, -1.21759143507263, -1.18442457113743, -1.15200309344505, -1.12029423371771, -1.08926733809709, -1.05889368905357, -1.02914634565952, -1, -0.971430847803229, -0.943416471633633, -0.915935735211525, -0.888968687611256, -0.862496476250065, -0.83650126771712, -0.810966175609983, -0.785875194647152, -0.761213140412883, -0.736965594166206, -0.713118852211838, -0.689659879387849, -0.666576266274808, -0.643856189774725, -0.62148837674627, -0.599462070416271, -0.577766999316952, -0.556393348524385, -0.535331732996556, -0.514573172829758, -0.494109070270043, -0.473931188332412, -0.454031630894707, -0.434402824145775, -0.415037499278844, -0.395928676331139, -0.377069649079823, -0.358453970912476, -0.340075441597622, -0.321928094887362, -0.3040061868901, -0.286304185156641, -0.2688167584278, -0.251538766995964, -0.234465253637023, -0.217591435072627, -0.200912693925996, -0.184424571137427, -0.168122758808327, -0.15200309344505, -0.136061549576028, -0.120294233717712, -0.104697378666693, -0.0892673380970872, -0.0740005814437767, -0.0588936890535684, -0.0439433475875971, -0.0291463456595165, -0.0144995696951151];
console.assert(log2UniformDistributionPercentiles.length === 99);